<script setup lang="ts">
const { t: $t } = useI18n()

import type { CreateDefectLogisticsTask } from '~/types'

export type CreateOrEditLogisticsDefectTaskPopupConfig = {
  mode: 'create'
  defectId: string
} | {
  mode: 'update'
  id: string
}
const props = defineProps<{
  config: CreateOrEditLogisticsDefectTaskPopupConfig
}>()
const emit = defineEmits<{
  (e: 'close'): void
}>()

const id = computed(() => {
  if (props.config.mode === 'update') {
    return props.config.id
  }
  return undefined
})

// Fetch logisticsTask by id
const { logisticsTask: queryLogisticsTask, customer: queryCustomer } = useQuery()
const { data: logisticsTask, isFetching: isFetchingLogisticsTask } = queryLogisticsTask.byIdOrThrow(id, 'defect')
const { data: customerOptions } = queryCustomer.allForSelect()
const isLogisticsTaskPlanned = computed(() => {
  if (!logisticsTask.value) {
    return false
  }
  return !!logisticsTask.value.plannedAt
})

const payload = ref<CreateDefectLogisticsTask>()
watch(logisticsTask, (task) => {
  if (payload.value) {
    return
  }
  if (props.config.mode === 'create') {
    // create payload for `create`
    payload.value = {
      type: 'defect',
      plannedDeliveryAt: new Date(),
      status: 'created',
      deliveryFrom: '',
      deliveryTo: '',
      defectId: props.config.defectId,
      workshopOfferId: '',
      remarks: '',
    }
    return
  }

  if (task && !isFetchingLogisticsTask.value) {
    // We check logisticsTask type of on the server,
    // but we need to cast type here to avoid TypeScript errors
    payload.value = task as CreateDefectLogisticsTask
  }
}, { immediate: true })

// Update or create logisticsTask
const { $trpc, queryClient, useMutation, makeTrpcErrorToast } = useMutationHelpers()
const notification = useNotification()
const create = useMutation({
  mutationFn: $trpc.logisticsTask.create.mutate,
  onError: makeTrpcErrorToast(notification, { description: $t('logisticsTask.notification.update.error') }),
  onSuccess: async () => {
    await queryClient.invalidateQueries({ queryKey: ['logisticsTask'] })
    notification.success({ title: $t('logisticsTask.notification.update.success'), duration: 4500 })
    emit('close')
  },
})
const update = useMutation({
  mutationFn: $trpc.logisticsTask.update.mutate,
  onError: makeTrpcErrorToast(notification, { description: $t('logisticsTask.notification.create.error') }),
  onSuccess: async (logisticsTask) => {
    await queryClient.invalidateQueries({ queryKey: ['logisticsTask'] })
    notification.success({ title: $t('logisticsTask.notification.create.success', { id: logisticsTask.id }), duration: 4500 })
    emit('close')
  },
})

function submit(data: CreateDefectLogisticsTask) {
  if (id.value) {
    update.mutate({ id: id.value, ...data })
  } else {
    create.mutate(data)
  }
}
</script>

<template>
  <ThePopup
    :title="$t(`defect.logisticsTask.${config.mode}.title`)"
    show
    @close="emit('close')"
  >
    <LogisticsDefectTaskCreateOrEditForm
      v-if="payload"
      :payload
      :customer-options="customerOptions ?? []"
      :is-logistics-task-planned
      @submit="submit"
    />
  </ThePopup>
</template>
